import React, { useCallback, useEffect, useRef, useState } from 'react';
import CompactNumberField from './CompactNumberField';

function clampValue(value, min, max) {
  let nextValue = value;
  if (typeof min === 'number') {
    nextValue = Math.max(min, nextValue);
  }
  if (typeof max === 'number') {
    nextValue = Math.min(max, nextValue);
  }
  return nextValue;
}

function parseDraftValue(draftValue, allowDecimal) {
  const trimmedValue = String(draftValue ?? '').trim();
  if (trimmedValue === '') {
    return null;
  }

  const parsedValue = allowDecimal
    ? Number(trimmedValue)
    : parseInt(trimmedValue, 10);

  return Number.isFinite(parsedValue) ? parsedValue : null;
}

export default function QuantityStepper({
  value,
  onCommit,
  onChange,
  min,
  max,
  step = 1,
  disabled = false,
  emptyValue = null,
  allowDecimal = false,
  allowNegative = false,
  normalizeValue = null,
  inputLabel = 'Value',
  decrementLabel = '−',
  incrementLabel = '+',
  decrementAriaLabel = null,
  incrementAriaLabel = null,
  style,
  inputStyle,
  buttonStyle,
  decrementButtonStyle,
  incrementButtonStyle,
}) {
  const draftValueRef = useRef(Number.isFinite(value) ? value : null);
  const [inputResetKey, setInputResetKey] = useState(0);

  useEffect(() => {
    draftValueRef.current = Number.isFinite(value) ? value : null;
  }, [value]);

  const commitClampedValue = useCallback((nextValue) => {
    const clampedValue = clampValue(nextValue, min, max);
    draftValueRef.current = clampedValue;
    if (!Object.is(clampedValue, value)) {
      onCommit(clampedValue);
    }
  }, [max, min, onCommit, value]);

  const stepValue = useCallback((delta) => {
    const baseValue = Number.isFinite(draftValueRef.current)
      ? draftValueRef.current
      : (Number.isFinite(value) ? value : 0);
    setInputResetKey((currentKey) => currentKey + 1);
    commitClampedValue(baseValue + delta);
  }, [commitClampedValue, value]);

  const handleInputChange = useCallback((event) => {
    draftValueRef.current = parseDraftValue(event.target.value, allowDecimal);
    if (onChange) onChange(event);
  }, [allowDecimal, onChange]);

  const effectiveValue = Number.isFinite(draftValueRef.current)
    ? draftValueRef.current
    : (Number.isFinite(value) ? value : 0);
  const decrementDisabled = disabled || (typeof min === 'number' && effectiveValue <= min);
  const incrementDisabled = disabled || (typeof max === 'number' && effectiveValue >= max);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        ...style,
      }}
    >
      <button
        type="button"
        onClick={() => stepValue(-step)}
        style={{
          width: '32px',
          height: '32px',
          padding: 0,
          marginBottom: 0,
          ...buttonStyle,
          ...decrementButtonStyle,
        }}
        aria-label={decrementAriaLabel || `Decrease ${inputLabel}`}
        disabled={decrementDisabled}
      >
        {decrementLabel}
      </button>
      <CompactNumberField
        key={inputResetKey}
        aria-label={inputLabel}
        value={value}
        onCommit={commitClampedValue}
        emptyValue={emptyValue}
        min={min}
        max={max}
        allowDecimal={allowDecimal}
        allowNegative={allowNegative}
        normalizeValue={normalizeValue}
        disabled={disabled}
        onChange={handleInputChange}
        style={inputStyle}
      />
      <button
        type="button"
        onClick={() => stepValue(step)}
        style={{
          width: '32px',
          height: '32px',
          padding: 0,
          marginBottom: 0,
          ...buttonStyle,
          ...incrementButtonStyle,
        }}
        aria-label={incrementAriaLabel || `Increase ${inputLabel}`}
        disabled={incrementDisabled}
      >
        {incrementLabel}
      </button>
    </div>
  );
}
